/**
 * Génération PDF rapport mensuel maintenance Royal LT (brief §4.5).
 */
import { TYPE_BT_LABELS, type RapportMensuelMaintenance } from '@/types';

interface JsPDFInternal {
  internal: { getNumberOfPages: () => number; pageSize: { height: number; width: number } };
  setFontSize: (size: number) => void;
  setFont: (family: string, style?: string) => void;
  setTextColor: (...args: number[]) => void;
  setFillColor: (...args: number[]) => void;
  rect: (x: number, y: number, w: number, h: number, style?: string) => void;
  text: (text: string | string[], x: number, y: number, options?: object) => void;
  addPage: () => void;
  save: (filename: string) => void;
  lastAutoTable?: { finalY: number };
}

const COLOR_PRIMARY: [number, number, number] = [192, 0, 0];
const COLOR_TX_DARK: [number, number, number] = [40, 40, 40];
const COLOR_TX_GREY: [number, number, number] = [110, 110, 110];

const fmtInt = (n: number, dec = 0) =>
  new Intl.NumberFormat('fr-FR', { maximumFractionDigits: dec, minimumFractionDigits: dec })
    .format(n)
    .replace(/[  ]/g, ' ');

export async function generateRapportMensuelMaintenancePDF(data: RapportMensuelMaintenance): Promise<void> {
  const { jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' }) as unknown as JsPDFInternal;
  const pageWidth = doc.internal.pageSize.width;

  // En-tête
  doc.setFillColor(...COLOR_PRIMARY);
  doc.rect(0, 0, pageWidth, 8, 'F');

  doc.setTextColor(...COLOR_TX_DARK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text('RAPPORT MENSUEL MAINTENANCE', 14, 20);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(...COLOR_PRIMARY);
  doc.text(data.filiale.fullName, 14, 27);

  doc.setFontSize(10);
  doc.setTextColor(...COLOR_TX_GREY);
  doc.text(`Période : ${data.periode.mois}`, 14, 34);

  doc.setFontSize(8);
  doc.text(`Généré le ${new Date(data.generatedAt).toLocaleString('fr-FR')}`, 14, 40);

  // Section 1 — Synthèse coûts
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...COLOR_TX_DARK);
  doc.text('1 · Synthèse coûts maintenance', 14, 50);

  autoTable(doc as unknown as object, {
    startY: 53,
    body: [
      ['Coût total mois',           `${fmtInt(data.statsCouts.coutTotalFcfa)} FCFA`],
      ['Coût moyen par camion',     `${fmtInt(data.statsCouts.coutMoyenParCamionFcfa)} FCFA`],
      ['Nb camions avec BT',         String(data.statsCouts.nbCamionsAvecBT)],
      ['Coût mois précédent',       data.statsCouts.coutMoisPrecedentFcfa !== null ? `${fmtInt(data.statsCouts.coutMoisPrecedentFcfa)} FCFA` : '—'],
      ['Évolution',                 data.statsCouts.evolutionPct !== null ? `${data.statsCouts.evolutionPct >= 0 ? '+' : ''}${data.statsCouts.evolutionPct.toFixed(1)}%` : '—'],
    ],
    margin: { left: 14, right: 14 },
    styles: { fontSize: 9, cellPadding: 3 },
    columnStyles: {
      0: { cellWidth: 75, fontStyle: 'bold', textColor: COLOR_TX_GREY },
      1: { halign: 'right', fontStyle: 'bold' },
    },
    theme: 'plain',
  } as Parameters<typeof autoTable>[1]);

  let cursorY = (doc.lastAutoTable?.finalY ?? 90) + 8;

  // Section 2 — BT par type
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('2 · Bons de Travail par type', 14, cursorY);
  autoTable(doc as unknown as object, {
    startY: cursorY + 2,
    head: [['Type', 'Nombre', 'Coût total (FCFA)']],
    body: data.btParType.map((b) => [TYPE_BT_LABELS[b.type], String(b.count), fmtInt(b.coutFcfa)]),
    margin: { left: 14, right: 14 },
    styles: { fontSize: 9, cellPadding: 3 },
    headStyles: { fillColor: COLOR_PRIMARY, textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [248, 248, 248] },
    columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' } },
  } as Parameters<typeof autoTable>[1]);
  cursorY = (doc.lastAutoTable?.finalY ?? cursorY) + 8;

  if (cursorY > 220) { doc.addPage(); cursorY = 20; }

  // Section 3 — Taux respect préventif
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('3 · Taux respect planning préventif', 14, cursorY);
  autoTable(doc as unknown as object, {
    startY: cursorY + 2,
    body: [
      ['Entrées planning total',  String(data.tauxRespectPreventif.totalEntries)],
      ['Réalisées / générées',    String(data.tauxRespectPreventif.realises)],
      ['Taux respect',            `${data.tauxRespectPreventif.pourcentage.toFixed(1)}%`],
    ],
    margin: { left: 14, right: 14 },
    styles: { fontSize: 9, cellPadding: 3 },
    columnStyles: {
      0: { cellWidth: 75, fontStyle: 'bold', textColor: COLOR_TX_GREY },
      1: { halign: 'right', fontStyle: 'bold' },
    },
    theme: 'plain',
  } as Parameters<typeof autoTable>[1]);
  cursorY = (doc.lastAutoTable?.finalY ?? cursorY) + 8;

  if (cursorY > 220) { doc.addPage(); cursorY = 20; }

  // Section 4 — Top pannes récurrentes
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('4 · Top pannes récurrentes (alerte qualité fournisseur)', 14, cursorY);
  if (data.topPannesRecurrentes.length === 0) {
    autoTable(doc as unknown as object, {
      startY: cursorY + 2,
      body: [['✅ Aucune pièce utilisée sur ≥ 2 camions distincts ce mois.']],
      margin: { left: 14, right: 14 },
      styles: { fontSize: 9, fontStyle: 'italic', textColor: [22, 163, 74] },
      theme: 'plain',
    } as Parameters<typeof autoTable>[1]);
  } else {
    autoTable(doc as unknown as object, {
      startY: cursorY + 2,
      head: [['Pièce', 'Camions distincts', 'Nb fois', 'Coût cumul']],
      body: data.topPannesRecurrentes.map((p) => [
        p.designation, String(p.nbCamionsDistincts), String(p.nbFois), fmtInt(p.coutCumulFcfa),
      ]),
      margin: { left: 14, right: 14 },
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: COLOR_PRIMARY, textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 248, 248] },
      columnStyles: { 1: { halign: 'right', fontStyle: 'bold' }, 2: { halign: 'right' }, 3: { halign: 'right' } },
    } as Parameters<typeof autoTable>[1]);
    cursorY = (doc.lastAutoTable?.finalY ?? cursorY) + 8;
  }

  if (cursorY > 220) { doc.addPage(); cursorY = 20; }

  // Section 5 — Top pièces consommées
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('5 · Top 10 pièces les plus utilisées', 14, cursorY);
  if (data.topPiecesConsommees.length === 0) {
    autoTable(doc as unknown as object, {
      startY: cursorY + 2,
      body: [['Aucune pièce enregistrée ce mois.']],
      margin: { left: 14, right: 14 },
      styles: { fontSize: 9, fontStyle: 'italic' },
      theme: 'plain',
    } as Parameters<typeof autoTable>[1]);
  } else {
    autoTable(doc as unknown as object, {
      startY: cursorY + 2,
      head: [['Pièce', 'Nb fois', 'Quantité totale', 'Coût cumul (FCFA)']],
      body: data.topPiecesConsommees.map((p) => [
        p.designation, String(p.nbFois), p.quantiteTotale.toString(), fmtInt(p.coutCumulFcfa),
      ]),
      margin: { left: 14, right: 14 },
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: COLOR_PRIMARY, textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 248, 248] },
      columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' } },
    } as Parameters<typeof autoTable>[1]);
    cursorY = (doc.lastAutoTable?.finalY ?? cursorY) + 8;
  }

  if (cursorY > 220) { doc.addPage(); cursorY = 20; }

  // Section 6 — Camions coût/tonne anormal
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(220, 38, 38);
  doc.text('6 · Camions ratio coût/tonne anormal (>2000 FCFA/T)', 14, cursorY);
  doc.setTextColor(...COLOR_TX_DARK);
  if (data.camionsCoutTonneAnormal.length === 0) {
    autoTable(doc as unknown as object, {
      startY: cursorY + 2,
      body: [['✅ Aucun camion avec coût/tonne anormal ce mois.']],
      margin: { left: 14, right: 14 },
      styles: { fontSize: 9, fontStyle: 'italic', textColor: [22, 163, 74] },
      theme: 'plain',
    } as Parameters<typeof autoTable>[1]);
  } else {
    autoTable(doc as unknown as object, {
      startY: cursorY + 2,
      head: [['Camion', 'Modèle', 'Coût total', 'Tonnes mois', 'Coût/tonne', 'Action']],
      body: data.camionsCoutTonneAnormal.map((c) => [
        c.camionCode, c.modele ?? '—', `${fmtInt(c.coutTotalFcfa)} F`,
        `${fmtInt(c.tonnesMois, 1)} T`, `${fmtInt(c.coutParTonne)} F/T`,
        c.gravite === 'reforme' ? 'RÉFORME RECOMMANDÉE' : 'Analyse obligatoire',
      ]),
      margin: { left: 14, right: 14 },
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: COLOR_PRIMARY, textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 248, 248] },
      columnStyles: {
        0: { fontStyle: 'bold' },
        2: { halign: 'right' }, 3: { halign: 'right' },
        4: { halign: 'right', fontStyle: 'bold', textColor: [220, 38, 38] },
        5: { fontStyle: 'bold', textColor: [220, 38, 38] },
      },
    } as Parameters<typeof autoTable>[1]);
  }

  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  const pageHeight = doc.internal.pageSize.height;
  for (let i = 1; i <= pageCount; i++) {
    (doc as unknown as { setPage: (n: number) => void }).setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(...COLOR_TX_GREY);
    doc.text(`${data.filiale.fullName} — Rapport mensuel maintenance — Document confidentiel`, 14, pageHeight - 8);
    doc.text(`Page ${i} / ${pageCount}`, pageWidth - 14, pageHeight - 8, { align: 'right' });
  }

  const filename = `Rapport_Maintenance_${data.filiale.name.replace(/\s+/g, '_')}_${data.periode.from.slice(0, 7)}.pdf`;
  doc.save(filename);
}
